<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { isAuthenticated, authStore } from './stores/auth';
  import { syncStore, isOnline } from './stores/sync';
  import { initRouter, navigateTo, type Route } from './utils/router';
  import Login from './components/Login.svelte';
  import ProductGrid from './components/ProductGrid.svelte';
  import Cart from './components/Cart.svelte';
  import Settings from './components/Settings.svelte';
  import Management from './components/Management.svelte';

  let error = $state<string | null>(null);
  let currentRoute = $state<Route>('/kasse');
  let isFullscreen = $state(false);

  // Router initialisieren
  let cleanupRouter: (() => void) | null = null;
  let cleanupFullscreen: (() => void) | null = null;
  let sessionRefreshInterval: number | null = null;

  onMount(() => {
    try {
      // Router initialisieren
      cleanupRouter = initRouter((route: Route) => {
        currentRoute = route;
      });

      // PWA install prompt handling
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('/sw.js').catch((err: unknown) => {
          console.warn('Service Worker registration failed:', err);
        });
      }

      // Initial Sync wenn online - nicht kritisch wenn fehlschlägt
      if (navigator.onLine) {
        syncStore.sync().catch((err: unknown) => {
          console.warn('Initial sync failed:', err);
        });
      }

      // Fullscreen Event Listener
      checkFullscreenStatus();
      const fullscreenChangeEvents = [
        'fullscreenchange',
        'webkitfullscreenchange',
        'mozfullscreenchange',
        'MSFullscreenChange'
      ];

      const handleFullscreenChange = () => {
        checkFullscreenStatus();
      };

      fullscreenChangeEvents.forEach((event) => {
        document.addEventListener(event, handleFullscreenChange);
      });

      cleanupFullscreen = () => {
        fullscreenChangeEvents.forEach((event) => {
          document.removeEventListener(event, handleFullscreenChange);
        });
      };

      // Automatischer Session-Refresh wird in $effect() außerhalb von onMount eingerichtet
    } catch (err) {
      error = err instanceof Error ? err.message : 'Unbekannter Fehler';
      console.error('App initialization error:', err);
    }
  });

  onDestroy(() => {
    if (cleanupRouter) {
      cleanupRouter();
    }
    if (cleanupFullscreen) {
      cleanupFullscreen();
    }
    if (sessionRefreshInterval !== null) {
      clearInterval(sessionRefreshInterval);
    }
  });

  function handleLogout() {
    try {
      authStore.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  }

  function handleNavigate(route: Route) {
    navigateTo(route);
  }

  // Prüfe ob Cart angezeigt werden soll (nur bei Kasse)
  const showCart = $derived(currentRoute === '/kasse');

  // Fullscreen Funktionen
  function checkFullscreenStatus() {
    isFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
  }

  async function toggleFullscreen() {
    try {
      const doc = document.documentElement as any;

      if (!isFullscreen) {
        // Enter fullscreen
        if (doc.requestFullscreen) {
          await doc.requestFullscreen();
        } else if (doc.webkitRequestFullscreen) {
          await doc.webkitRequestFullscreen();
        } else if (doc.mozRequestFullScreen) {
          await doc.mozRequestFullScreen();
        } else if (doc.msRequestFullscreen) {
          await doc.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }

      checkFullscreenStatus();
    } catch (error) {
      console.error('Fullscreen toggle error:', error);
    }
  }

  // Automatischer Session-Refresh mit $effect (Svelte 5 Rune)
  // Erneuere Session alle 12 Stunden (vor Ablauf nach 24h)
  $effect(() => {
    if (!$isAuthenticated) {
      // Wenn nicht eingeloggt, lösche bestehenden Interval
      if (sessionRefreshInterval !== null) {
        clearInterval(sessionRefreshInterval);
        sessionRefreshInterval = null;
      }
      return;
    }

    // Lösche bestehenden Interval falls vorhanden
    if (sessionRefreshInterval !== null) {
      clearInterval(sessionRefreshInterval);
    }

    // Erneuere Session alle 12 Stunden (12 * 60 * 60 * 1000 ms)
    const REFRESH_INTERVAL = 12 * 60 * 60 * 1000;

    sessionRefreshInterval = window.setInterval(async () => {
      try {
        await authStore.refreshSession();
        console.log('Session automatisch erneuert');
      } catch (error) {
        console.warn('Automatische Session-Erneuerung fehlgeschlagen:', error);
      }
    }, REFRESH_INTERVAL);

    // Erste Erneuerung nach 1 Stunde (um sicherzustellen, dass Session aktiv bleibt)
    const firstRefreshTimeout = setTimeout(
      async () => {
        try {
          await authStore.refreshSession();
          console.log('Erste Session-Erneuerung durchgeführt');
        } catch (error) {
          console.warn('Erste Session-Erneuerung fehlgeschlagen:', error);
        }
      },
      60 * 60 * 1000
    ); // 1 Stunde

    // Cleanup-Funktion
    return () => {
      if (sessionRefreshInterval !== null) {
        clearInterval(sessionRefreshInterval);
        sessionRefreshInterval = null;
      }
      clearTimeout(firstRefreshTimeout);
    };
  });
</script>

{#if error}
  <div class="min-h-screen flex items-center justify-center bg-red-50 p-4">
    <div class="bg-white rounded-lg shadow-lg p-6 max-w-md">
      <h1 class="text-xl font-bold text-red-900 mb-2">Fehler</h1>
      <p class="text-red-700 mb-4">{error}</p>
      <button
        onclick={() => window.location.reload()}
        class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
      >
        Seite neu laden
      </button>
    </div>
  </div>
{:else if !$isAuthenticated}
  <Login />
{:else}
  <main class="min-h-screen bg-gray-50 {showCart ? 'pr-96' : ''}">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">ML POS System</h1>
              <p class="text-sm text-gray-600">
                {$authStore.user?.full_name || $authStore.user?.name || 'Benutzer'}
              </p>
            </div>
            <!-- Online Status -->
            <div class="flex items-center space-x-2">
              {#if $isOnline}
                <div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span class="text-sm text-gray-700">Online</span>
              {:else}
                <div class="w-3 h-3 bg-red-500 rounded-full"></div>
                <span class="text-sm text-gray-700">Offline</span>
              {/if}
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <!-- Navigation -->
            <nav class="flex items-center space-x-1 mr-4">
              <button
                onclick={() => handleNavigate('/kasse')}
                class="px-4 py-2 text-sm font-medium rounded-lg transition-colors {currentRoute ===
                '/kasse'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}"
              >
                Kasse
              </button>
              <button
                onclick={() => handleNavigate('/management')}
                class="px-4 py-2 text-sm font-medium rounded-lg transition-colors {currentRoute ===
                '/management'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}"
              >
                Management
              </button>
              <button
                onclick={() => handleNavigate('/einstellungen')}
                class="px-4 py-2 text-sm font-medium rounded-lg transition-colors {currentRoute ===
                '/einstellungen'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}"
              >
                Einstellungen
              </button>
            </nav>
            <!-- Fullscreen Button -->
            <button
              onclick={toggleFullscreen}
              class="p-2 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              title={isFullscreen ? 'Vollbild verlassen' : 'Vollbild aktivieren'}
              aria-label={isFullscreen ? 'Vollbild verlassen' : 'Vollbild aktivieren'}
            >
              {#if isFullscreen}
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 9V4.5M9 9H4.5M9 15v4.5M9 15H4.5M15 9h4.5M15 9V4.5M15 15h4.5M15 15v4.5"
                  />
                </svg>
              {:else}
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  />
                </svg>
              {/if}
            </button>
            <button
              onclick={handleLogout}
              class="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Abmelden
            </button>
          </div>
        </div>
      </div>
    </header>

    {#if currentRoute === '/kasse'}
      <!-- Main Content -->
      <div class="container mx-auto px-4 py-4 pb-8">
        <ProductGrid />
      </div>

      <!-- Cart - Immer sichtbar rechts (Registrierkasse) -->
      <Cart />
    {:else if currentRoute === '/management'}
      <!-- Management View - Volle Breite -->
      <Management />
    {:else if currentRoute === '/einstellungen'}
      <!-- Settings View - Volle Breite -->
      <Settings />
    {/if}
  </main>
{/if}

<style>
  /* Global styles */
</style>
